import * as t from "./actionTypes";
import { cameraClustersService } from "client-app-core";

export const addCameraCluster = (cluster) => {
	return {
		type: t.ADD_CAMERA_CLUSTER,
		payload: { cluster }
	};
};

export const updateCameraCluster = (cluster) => {
	return {
		type: t.UPDATE_CAMERA_CLUSTER,
		payload: { cluster }
	};
};

const InitialcameraClustersBatchReceived = (clusters) => {
	return {
		type: t.INITIAL_CAMERA_CLUSTERS_BATCH_RECEIVED,
		payload: {
			clusters
		}
	};
};

export const cameraClusterRemoved = (clusterId) => {
	return {
		type: t.CAMERA_CLUSTER_REMOVED,
		payload: {
			clusterId
		}
	};
};

export const cameraClusterUpdated = (clusterId, cluster) => {
	return {
		type: t.CAMERA_CLUSTER_UPDATED,
		payload: {
			clusterId,
			cluster
		}
	};
};

function updateCamerasMatrix(cameras, updates) {
	const updated = JSON.parse(JSON.stringify(cameras)); // deep clone to avoid mutating original

	for (const key in updates) {
		const { row, col, camera } = updates[key];
		if (updated[row]) {
			updated[row][col] = camera;
		} else {
			console.warn(`Row ${row} doesn't exist in cameras`);
		}
	}

	return updated;
}

export const getAllCameraClusters = () => {
	return (dispatch, getState) => {
		// stream cameraClusters
		cameraClustersService.subscribeCameraClusters((err, res) => {
			if (err) {
				console.log("Get all camera clusters error:", err);
			} else {
				const initialCameraClusterBatch = [];
				res.forEach((change) => {
					if (change.type === "initial") {
						initialCameraClusterBatch.push(change.new_val);
					}
					if (change.type === "add") {
						const cameraCluster = change.new_val;

						dispatch(addCameraCluster(cameraCluster));
					} else if (change.type === "change") {
						/**
						 * This logic ensures that unsaved temporary camera placements in a cluster are not lost
						 * when new cluster data is received (e.g., from a changefeed or stream).
						 * It compares the number of cameras in the incoming cluster data with the current data in the Redux store.
						 * If the new data has fewer or the same number of cameras, it assumes some temporary placements may be missing.
						 * In that case, it restores the previous temporary layout (rows and columns) and merges any unsaved temporary cameras
						 * stored in a global variable back into the cluster's camera matrix.
						 * Finally, it updates the Redux store with the complete, merged data to persist the current state.
						 */

						// Get the updated cluster data coming from the stream (e.g., changefeed)
						const cameraCluster = change.new_val;

						// Get the existing cluster data from the Redux reducer using the cluster ID
						const selectedCluster = getState().globalData.cameraClusters[cameraCluster.id] || {};

						// Count the number of valid cameras in the existing (reducer) cluster
						const tempCameras =
							selectedCluster?.cameras
								.flat() // Flatten the nested camera array by one level
								.filter((item) => item && typeof item === "object" && item.cameraId).length || 0;

						// Count the number of valid cameras in the new incoming cluster data
						const newCameras =
							change?.new_val?.cameras
								.flat() // Flatten the nested camera array by one level
								.filter((item) => item && typeof item === "object" && item.cameraId).length || 0;

						// Compare camera counts: if new cluster has fewer or equal cameras than the existing cluster
						if (newCameras < tempCameras || newCameras === tempCameras) {
							// Copy tempColumns from the existing cluster to retain previous layout info
							cameraCluster.tempColumns = selectedCluster.tempColumns;

							// Copy tempRows from the existing cluster
							cameraCluster.tempRows = selectedCluster.tempRows;

							// Set flag to false so reducer does not override the temporary row/column layout
							cameraCluster.updateTemps = false;

							// Merge any unsaved temporary cameras (persisted in a global variable) into the new cluster data
							if (global?.camera_wall_tempClusterCams?.[selectedCluster.id]) {
								cameraCluster.cameras = updateCamerasMatrix(
									cameraCluster.cameras,
									global?.camera_wall_tempClusterCams?.[selectedCluster.id]
								);
							}
						}

						// Dispatch the updated cameraCluster to Redux
						dispatch(updateCameraCluster(cameraCluster));

						// Dispatch the updated camera layout to update camera slot positions in Redux
						dispatch(UpdateClusterCameraSlots(cameraCluster.id, cameraCluster?.cameras));
					} else if (change.type === "remove") {
						const cameraCluster = change.old_val;

						dispatch(cameraClusterRemoved(cameraCluster.id));
						dispatch(RemoveCameraClusters([cameraCluster.id]));
					}
				});
				if (initialCameraClusterBatch.length > 0) {
					dispatch(InitialcameraClustersBatchReceived(initialCameraClusterBatch));
				}
			}
		});
	};
};

// For now, the actions below are used only in the camera-wall app.

export const RemoveCameraClusters = (clusterIds) => {
	return {
		type: t.REMOVE_CAMERA_CLUSTERS,
		payload: {
			clusterIds
		}
	};
};

export const UpdateClusterCameraSlots = (clusterId, cameras) => {
	return {
		type: t.UPDATE_CLUSTER_CAMERASLOTS,
		payload: { clusterId, cameras }
	};
};

export const updateCamerClusterRowsAndColumns = (clusterId, rows, columns) => {
	return {
		type: t.UPDATE_CAMERA_CLUSTER_ROWS_AND_COLUMNS,
		payload: { clusterId, rows, columns }
	};
};

export const resetCameraClusterRowsAndColsToDefault = (clusterId) => {
	return {
		type: t.RESET_CAMERA_CLUSTER_ROWS_AND_COLUMNS_TO_DEFAULT,
		payload: { clusterId }
	};
};
